// Module
import { uniqBy, groupBy } from "lodash";

// Constant
import { Filters as FilterConstant } from "constants/filters";
import { RoleManagement as Constants, NewGroup, MONTHLY_DECLARATIONS } from "constants/global";

// Type
import { TableDataType } from "components/atoms/ExpandedRowTable/ExpandedRowTable";
import { ApiUserResponse, OutletItem } from "services/api/types";
import type { GetReimbSummaryResponse } from "services/api/types";
import type { GroupDetails } from "containers/ReimbursementVerification/useReimbursementVerification";
import { PermissionGroup } from "containers/NewGroup/useNewGroup";

// This method will show the MFA status for user under Role Management page
export const get2FAStatus = (status: string) => {
	switch (status) {
		case "X":
			return "Yes";
		case "":
			return "No";
	}
};

// This method will show the verifier status in review page of declaration approval
export const getVerifierStatus = (status: string) => {
	switch (status) {
		case "X":
			return "Rejected";
		case "":
			return "Approved";
	}
};

// This method will be used to get the rows per page
export const getRowsPerPage = (selectedFilter: TableDataType, selectedGroup: string): number => {
	if (selectedFilter && selectedGroup && selectedFilter[selectedGroup] && selectedFilter[selectedGroup] === FilterConstant.ROLE_MANAGEMENT[1].values[1].value) {
		return Constants.ROW_PER_PAGE_10;
	}
	return Constants.DEFAULT_ROW_PER_PAGE;
};

export const getRowsPerPageForMonthlyDeclaration = (selectedFilter: TableDataType, selectedGroup: string): number => {
	if (selectedFilter && selectedGroup && selectedFilter[selectedGroup] && selectedFilter[selectedGroup] === FilterConstant.REIMB_LIST[1].values[1].value) {
		return MONTHLY_DECLARATIONS.ROW_PER_PAGE_30;
	}
	return MONTHLY_DECLARATIONS.DEFAULT_ROW_PER_PAGE;
};

export const getUserPermissionList = (payload: ApiUserResponse) => {
	const permissionList: any = [];
	payload.data.map((item: any) => {
		item.groups.map((groupItem: any) => {
			groupItem.permissions.map((permissionItem: any) => {
				permissionList.push(permissionItem);
			});
		});
	});
	return permissionList;
};

export const getReimbVoucherGroup = (payload: GetReimbSummaryResponse) => {
	const list: Array<GroupDetails> = [];
	payload.data?.reimb_summ.map((item) => {
		list.push({
			voucher_group_code: parseInt(item.voucher_group_code),
			voucher_group_desc: item.voucher_group_desc
		});
	});
	return uniqBy(list, "voucher_group_code");
};

export const checkForMultipleLimits = (roles: string[], permissionsList: PermissionGroup[]) => {
	const hasMultiple = {
		retroClaim: false,
		reimbEVApproval: false,
		reimbApproval: false,
		reimbNonCashApproval: false,
		reimbNonMemberApproval: false
	};

	const LIMIT_PERM_CODES = [
		NewGroup.RETRO_CLAIM_PERM_GROUP,
		NewGroup.REIMB_EV_APPROVAL_PERM_GROUP,
		NewGroup.REIMB_APPROVAL_PERM_GROUP,
		NewGroup.REIMB_NON_CASH_APP_LIMIT,
		NewGroup.REIMB_NON_MEMBER_EV_APP_LIMIT
	];

	const permissions = permissionsList
		?.filter((permission) => {
			if (permission?.code && LIMIT_PERM_CODES?.includes(permission?.code)) {
				return permission;
			}
		})
		.map((perm) => perm?.values)
		.flat();

	const selectedPermissions = permissions.filter((perm) => {
		if (perm?.perm_group_code && roles?.indexOf(perm?.perm_code) > -1) {
			return perm;
		}
	});

	const groupedPermissions = groupBy(selectedPermissions, "perm_group_code");

	if (groupedPermissions[NewGroup.RETRO_CLAIM_PERM_GROUP]?.length > 1) {
		hasMultiple.retroClaim = true;
	}
	if (groupedPermissions[NewGroup.REIMB_EV_APPROVAL_PERM_GROUP]?.length > 1) {
		hasMultiple.reimbEVApproval = true;
	}
	if (groupedPermissions[NewGroup.REIMB_APPROVAL_PERM_GROUP]?.length > 1) {
		hasMultiple.reimbApproval = true;
	}
	if (groupedPermissions[NewGroup.REIMB_NON_CASH_APP_LIMIT]?.length > 1) {
		hasMultiple.reimbNonCashApproval = true;
	}
	if (groupedPermissions[NewGroup.REIMB_NON_MEMBER_EV_APP_LIMIT]?.length > 1) {
		hasMultiple.reimbNonMemberApproval = true;
	}

	return {
		...hasMultiple
	};
};

export function getOutlets(outletsList: OutletItem[], tenantName: { name: string; input: boolean }, outletName: { name: string; input?: boolean }) {
	if (!tenantName?.name && !outletName?.name) {
		return outletsList?.map((outlet: OutletItem) => `${outlet.outlet_code}`);
	}

	if (tenantName?.name && tenantName?.input) {
		return outletsList?.reduce((acc: string[], outlet: OutletItem) => {
			if (outlet?.tenant_code === tenantName?.name) {
				acc?.push(`${outlet.outlet_code}`);
			}
			return acc;
		}, []);
	}

	return [`${outletName?.name}`];
}
